import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, X, Info, CheckCircle, AlertCircle } from 'lucide-react';

// Types
export type ConfirmationType = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmationOptions {
  type?: ConfirmationType;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonVariant?: 'primary' | 'danger';
  icon?: React.ReactNode;
}

interface ConfirmationState extends ConfirmationOptions {
  isOpen: boolean;
  resolve: ((value: boolean) => void) | null;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
  confirmDelete: (itemName?: string) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

// Provider Component
export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    type: 'warning',
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        type: options.type || 'warning',
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        confirmButtonVariant: options.confirmButtonVariant,
        icon: options.icon,
        resolve,
      });
    });
  }, []);

  const confirmDelete = useCallback((itemName?: string): Promise<boolean> => {
    return confirm({
      type: 'danger',
      title: 'Confirm Delete',
      message: itemName
        ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
        : 'Are you sure you want to delete this item? This action cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmButtonVariant: 'danger',
    });
  }, [confirm]);

  const handleClose = (result: boolean) => {
    if (state.resolve) {
      state.resolve(result);
    }
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  return (
    <ConfirmationContext.Provider value={{ confirm, confirmDelete }}>
      {children}
      <ConfirmationModal state={state} onClose={handleClose} />
    </ConfirmationContext.Provider>
  );
};

// Hook
export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

// Modal Component
const ConfirmationModal: React.FC<{
  state: ConfirmationState;
  onClose: (result: boolean) => void;
}> = ({ state, onClose }) => {
  const typeConfig = {
    danger: {
      icon: <Trash2 className="w-6 h-6" />,
      iconBg: 'bg-red-500/10',
      iconBorder: 'border-red-500/20',
      iconColor: 'text-red-400',
    },
    warning: {
      icon: <AlertTriangle className="w-6 h-6" />,
      iconBg: 'bg-amber-500/10',
      iconBorder: 'border-amber-500/20',
      iconColor: 'text-amber-400',
    },
    info: {
      icon: <Info className="w-6 h-6" />,
      iconBg: 'bg-blue-500/10',
      iconBorder: 'border-blue-500/20',
      iconColor: 'text-blue-400',
    },
    success: {
      icon: <CheckCircle className="w-6 h-6" />,
      iconBg: 'bg-emerald-500/10',
      iconBorder: 'border-emerald-500/20',
      iconColor: 'text-emerald-400',
    },
  };

  const config = typeConfig[state.type || 'warning'];
  const confirmButtonVariant = state.confirmButtonVariant || (state.type === 'danger' ? 'danger' : 'primary');

  return (
    <AnimatePresence>
      {state.isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[10002]"
            onClick={() => onClose(false)}
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-[10003] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative p-6 pb-0">
                <button
                  onClick={() => onClose(false)}
                  className="absolute top-4 right-4 p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/10 transition-all"
                  aria-label="Close dialog"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${config.iconBg} border ${config.iconBorder} ${config.iconColor} mb-4`}>
                  {state.icon || config.icon}
                </div>

                {/* Title */}
                <h2 className="text-xl font-bold text-white pr-8">{state.title}</h2>
              </div>

              {/* Content */}
              <div className="p-6 pt-3">
                <p className="text-slate-400 leading-relaxed">{state.message}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 p-6 pt-0">
                <button
                  onClick={() => onClose(false)}
                  className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-xl transition-all active:scale-95 border border-white/5"
                >
                  {state.cancelLabel}
                </button>
                <button
                  onClick={() => onClose(true)}
                  className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all active:scale-95 ${
                    confirmButtonVariant === 'danger'
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                  autoFocus
                >
                  {state.confirmLabel}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

// Standalone Modal Component (for non-context usage)
interface StandaloneConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type?: ConfirmationType;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonVariant?: 'primary' | 'danger';
  loading?: boolean;
}

export const StandaloneConfirmationModal: React.FC<StandaloneConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type = 'warning',
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmButtonVariant,
  loading = false,
}) => {
  const typeConfig = {
    danger: {
      icon: <Trash2 className="w-6 h-6" />,
      iconBg: 'bg-red-500/10',
      iconBorder: 'border-red-500/20',
      iconColor: 'text-red-400',
    },
    warning: {
      icon: <AlertTriangle className="w-6 h-6" />,
      iconBg: 'bg-amber-500/10',
      iconBorder: 'border-amber-500/20',
      iconColor: 'text-amber-400',
    },
    info: {
      icon: <Info className="w-6 h-6" />,
      iconBg: 'bg-blue-500/10',
      iconBorder: 'border-blue-500/20',
      iconColor: 'text-blue-400',
    },
    success: {
      icon: <CheckCircle className="w-6 h-6" />,
      iconBg: 'bg-emerald-500/10',
      iconBorder: 'border-emerald-500/20',
      iconColor: 'text-emerald-400',
    },
  };

  const config = typeConfig[type];
  const buttonVariant = confirmButtonVariant || (type === 'danger' ? 'danger' : 'primary');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[10002]"
            onClick={onClose}
          />

          <div className="fixed inset-0 flex items-center justify-center z-[10003] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative p-6 pb-0">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/10 transition-all"
                  aria-label="Close dialog"
                >
                  <X className="w-4 h-4" />
                </button>

                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${config.iconBg} border ${config.iconBorder} ${config.iconColor} mb-4`}>
                  {config.icon}
                </div>

                <h2 className="text-xl font-bold text-white pr-8">{title}</h2>
              </div>

              <div className="p-6 pt-3">
                <p className="text-slate-400 leading-relaxed">{message}</p>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 pt-0">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-xl transition-all active:scale-95 border border-white/5 disabled:opacity-50"
                >
                  {cancelLabel}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={loading}
                  className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 ${
                    buttonVariant === 'danger'
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                  autoFocus
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    confirmLabel
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
